/**
 * src/components/ui/index.js — Premium "Luxury Pyrotechnics" design system
 */
import React, { useEffect, useRef, useState, forwardRef } from 'react';

// Ripple effect
function createRipple(e, el) {
  const rect   = el.getBoundingClientRect();
  const size   = Math.max(rect.width, rect.height) * 2;
  const x      = e.clientX - rect.left  - size / 2;
  const y      = e.clientY - rect.top   - size / 2;
  const ripple = document.createElement('span');
  Object.assign(ripple.style, {
    position:'absolute', width:`${size}px`, height:`${size}px`,
    left:`${x}px`, top:`${y}px`, borderRadius:'50%',
    background:'rgba(255,255,255,0.22)', transform:'scale(0)',
    pointerEvents:'none', zIndex:'0',
  });
  const anim = ripple.animate(
    [{ transform:'scale(0)', opacity:1 },{ transform:'scale(1)', opacity:0 }],
    { duration:550, easing:'ease-out' }
  );
  el.appendChild(ripple);
  anim.onfinish = () => ripple.remove();
}

// ── Spinner ───────────────────────────────────────────────────────────────────
export function Spinner({ size='md', className='' }) {
  const s = { sm:'w-4 h-4 border-[2px]', md:'w-8 h-8 border-2', lg:'w-12 h-12 border-[3px]' }[size];
  return <div className={`${s} border-red-200 border-t-red-600 rounded-full animate-spin ${className}`}/>;
}

export function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center animate-fade-in">
        <div className="relative mx-auto w-16 h-16 mb-5">
          <div className="absolute inset-0 border-2 border-red-100 rounded-full"/>
          <div className="absolute inset-0 border-2 border-transparent border-t-yellow-400 rounded-full animate-spin"/>
          <div className="absolute inset-2 border-2 border-transparent border-t-red-600 rounded-full animate-spin"
            style={{animationDirection:'reverse',animationDuration:'0.8s'}}/>
          <span className="absolute inset-0 flex items-center justify-center text-xl">🪔</span>
        </div>
        <p className="text-sm text-red-400/70 font-semibold tracking-widest uppercase">Loading</p>
      </div>
    </div>
  );
}

// ── Button with ripple ─────────────────────────────────────────────────────────
const BTN_VARIANTS = {
  primary:   'bg-gradient-to-br from-red-600 to-red-800 text-white shadow-lg hover:shadow-red-600/30 hover:shadow-xl',
  secondary: 'bg-gradient-to-br from-yellow-400 to-amber-500 text-red-900 shadow-md hover:shadow-yellow-400/30 hover:shadow-lg',
  gold:      'bg-gradient-to-br from-yellow-400 via-amber-400 to-yellow-500 text-red-900 font-black shadow-lg',
  outline:   'border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white',
  ghost:     'text-red-600 hover:bg-red-50',
  danger:    'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200',
  dark:      'bg-gray-900 hover:bg-gray-800 text-white shadow-md',
};
const BTN_SIZES = {
  sm:'px-3.5 py-1.5 text-xs', md:'px-5 py-2.5 text-sm',
  lg:'px-7 py-3.5 text-base', xl:'px-10 py-4 text-lg',
};

export const Button = forwardRef(function Button(
  { children, variant='primary', size='md', loading=false, disabled=false, className='', icon, onClick, ...props },
  ref
) {
  const btnRef = useRef(null);
  const combinedRef = ref || btnRef;

  const handleClick = (e) => {
    if (combinedRef?.current && !disabled && !loading) createRipple(e, combinedRef.current);
    onClick?.(e);
  };

  return (
    <button
      ref={combinedRef}
      disabled={disabled || loading}
      onClick={handleClick}
      className={`
        relative inline-flex items-center justify-center gap-2.5 font-bold rounded-xl overflow-hidden
        transition-all duration-200 cursor-pointer select-none
        active:scale-95 hover:-translate-y-0.5
        disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:active:scale-100
        ${BTN_VARIANTS[variant] || BTN_VARIANTS.primary}
        ${BTN_SIZES[size]       || BTN_SIZES.md}
        ${className}
      `}
      {...props}
    >
      {loading ? <Spinner size="sm" className="border-white/30 border-t-white"/> : icon && <span className="shrink-0">{icon}</span>}
      <span className="relative z-[1]">{children}</span>
    </button>
  );
});

// ── Input ──────────────────────────────────────────────────────────────────────
export function Input({ label, error, hint, className='', containerClass='', required, ...props }) {
  return (
    <div className={`space-y-1.5 ${containerClass}`}>
      {label && (
        <label className="block text-sm font-semibold text-gray-700">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <input
        className={`
          w-full rounded-xl border px-4 py-3 text-sm text-gray-800 placeholder-gray-400
          outline-none transition-all duration-200
          focus:ring-2 focus:ring-yellow-400/30 focus:border-yellow-500
          ${error ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'}
          ${className}
        `}
        {...props}
      />
      {error && <p className="flex items-center gap-1 text-xs text-red-600 font-medium animate-fade-in"><span>⚠</span> {error}</p>}
      {hint && !error && <p className="text-xs text-gray-400">{hint}</p>}
    </div>
  );
}

export function Textarea({ label, error, className='', required, ...props }) {
  return (
    <div className="space-y-1.5">
      {label && (
        <label className="block text-sm font-semibold text-gray-700">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <textarea
        className={`
          w-full rounded-xl border px-4 py-3 text-sm text-gray-800
          placeholder-gray-400 resize-none outline-none
          transition-all duration-200
          focus:ring-2 focus:ring-yellow-400/30 focus:border-yellow-500
          ${error ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'}
          ${className}
        `}
        {...props}
      />
      {error && <p className="flex items-center gap-1 text-xs text-red-600 font-medium animate-fade-in"><span>⚠</span> {error}</p>}
    </div>
  );
}

export function Select({ label, error, options=[], className='', required, ...props }) {
  return (
    <div className="space-y-1.5">
      {label && (
        <label className="block text-sm font-semibold text-gray-700">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <select
        className={`
          w-full rounded-xl border px-4 py-3 text-sm text-gray-800 bg-white
          outline-none cursor-pointer transition-all duration-200
          focus:ring-2 focus:ring-yellow-400/30 focus:border-yellow-500
          ${error ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'}
          ${className}
        `}
        {...props}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {error && <p className="text-xs text-red-600 font-medium">{error}</p>}
    </div>
  );
}

// ── Badge ──────────────────────────────────────────────────────────────────────
const BADGE_MAP = {
  red:    'bg-red-100 text-red-700 border border-red-200',
  green:  'bg-green-100 text-green-700 border border-green-200',
  amber:  'bg-amber-100 text-amber-700 border border-amber-200',
  blue:   'bg-blue-100 text-blue-700 border border-blue-200',
  purple: 'bg-purple-100 text-purple-700 border border-purple-200',
  gray:   'bg-gray-100 text-gray-600 border border-gray-200',
  indigo: 'bg-indigo-100 text-indigo-700 border border-indigo-200',
  gold:   'bg-yellow-100 text-yellow-700 border border-yellow-300',
};

export function Badge({ children, color='gray', className='' }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold tracking-wide ${BADGE_MAP[color]||BADGE_MAP.gray} ${className}`}>
      {children}
    </span>
  );
}

// ── Modal with animated entrance ──────────────────────────────────────────────
export function Modal({ open, onClose, title, children, maxWidth='max-w-lg' }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      const t = requestAnimationFrame(() => setShow(true));
      return () => cancelAnimationFrame(t);
    } else {
      setShow(false);
      const t = setTimeout(() => { document.body.style.overflow = ''; }, 300);
      return () => { clearTimeout(t); document.body.style.overflow = ''; };
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/65 backdrop-blur-sm transition-opacity duration-300 ${show ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />
      {/* Panel */}
      <div className={`
        relative bg-white rounded-3xl shadow-2xl w-full ${maxWidth} max-h-[92vh] overflow-y-auto
        border border-yellow-200/40
        transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]
        ${show ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'}
      `}>
        {/* Gold accent stripe */}
        <div className="absolute top-0 left-10 right-10 h-0.5 bg-gradient-to-r from-transparent via-yellow-400 to-transparent rounded-full"/>

        {title && (
          <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100">
            <h2 className="text-xl font-black text-gray-900" style={{fontFamily:'"Playfair Display",serif'}}>{title}</h2>
            <button onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-red-50 text-gray-400 hover:text-red-600 transition-all text-xl">
              ×
            </button>
          </div>
        )}
        <div className="p-7">{children}</div>
      </div>
    </div>
  );
}

// ── Confirm Dialog ─────────────────────────────────────────────────────────────
export function ConfirmDialog({ open, onClose, onConfirm, title, message, confirmLabel='Confirm', loading }) {
  return (
    <Modal open={open} onClose={onClose} maxWidth="max-w-sm">
      <div className="text-center">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center text-3xl mx-auto mb-5">⚠️</div>
        <h3 className="text-xl font-black text-gray-900 mb-2" style={{fontFamily:'"Playfair Display",serif'}}>{title}</h3>
        <p className="text-sm text-gray-500 leading-relaxed mb-7">{message}</p>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="primary" className="flex-1" onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Empty State ────────────────────────────────────────────────────────────────
export function EmptyState({ icon='📭', title, message, action }) {
  return (
    <div className="text-center py-24 px-4 animate-fade-in">
      <div className="text-7xl mb-5 opacity-80">{icon}</div>
      <h3 className="text-2xl font-black text-gray-700 mb-2" style={{fontFamily:'"Playfair Display",serif'}}>{title}</h3>
      {message && <p className="text-gray-400 text-sm max-w-xs mx-auto mb-7 leading-relaxed">{message}</p>}
      {action}
    </div>
  );
}

// ── Card ───────────────────────────────────────────────────────────────────────
export function Card({ children, className='', hover=false }) {
  return (
    <div className={`
      bg-white rounded-2xl border border-gray-100
      shadow-[0_2px_12px_rgba(0,0,0,0.06)]
      ${hover ? 'transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(192,21,42,0.12)] hover:border-yellow-200 cursor-pointer' : ''}
      ${className}
    `}>
      {children}
    </div>
  );
}

// ── Stat card with animated counter ───────────────────────────────────────────
export function StatCard({ icon, label, value, sub, accentColor='border-red-500' }) {
  const [animated, setAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setAnimated(true); io.disconnect(); } },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className={`bg-white rounded-2xl border-l-4 ${accentColor} border border-gray-100 p-6 shadow-[0_2px_12px_rgba(0,0,0,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(192,21,42,0.1)]`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">{label}</p>
          <p className={`text-3xl font-black text-gray-900 transition-all duration-700 ${animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
            style={{fontFamily:'"Playfair Display",serif',transitionDelay:'100ms'}}>
            {value}
          </p>
          {sub && <p className="text-xs text-gray-400 mt-1.5 font-medium">{sub}</p>}
        </div>
        <div className="text-4xl opacity-70 flex-shrink-0 mt-1">{icon}</div>
      </div>
    </div>
  );
}
